import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * CardList component renders a list of cards with optional image, title, and dynamic fields.
 *
 * @param {Object[]} headers - Array of header objects.
 * @param {string} headers[].field - The field name corresponding to the data key.
 * @param {string} headers[].label - The display label for the field.
 * @param {string} [imageField] - Optional. The field name that contains the image URL.
 * @param {string} [titleField] - Optional. The field name that contains the card title.
 * @param {Object[]} data - Array of data objects to be displayed in the cards.
 * @param {Function} [onCardClick] - Optional callback function to handle card click events.
 * @param {Function} [renderCard] - Optional custom function to render a card. Receives the item and index as arguments.
 * @param {string} [containerClassName] - Optional custom class name for the container div.
 * @param {boolean} [loading] - Optional flag to indicate if the data is loading.
 *
 * @returns {JSX.Element} The rendered card list component.
 */
const DataCard = ({
    headers,
    data,
    imageField = null,
    titleField = null,
    onCardClick = null,
    renderCard = null,
    containerClassName = 'card-list-container',
    loading = false
}) => {
    const { t } = useTranslation();

    const defaultRenderCard = (item) => (
        <div className="card-content">
            {/* {imageField && item[imageField] && (
                <div className="card-image">
                    <img src={item[imageField]} alt="card-img" />
                </div>
            )} */}
            <div className="card-details">
                {/* {titleField && item[titleField] && (
                    <h3 className="card-title">{item[titleField]}</h3>
                )} */}
                {headers.map((header, idx) => (
                    <div key={idx} className="card-field">
                        <strong>{t(header.label)}:</strong> {item[header.field]}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        loading ? (
            <div className="spinner-container">
                <div className="spinner"></div>
            </div>
        ) : (
            <div className={containerClassName}>
                {data?.map((item, index) => (
                    <div
                        key={index}
                        className="card"
                        style={{ cursor: onCardClick ? 'pointer' : 'default' }}
                        onClick={onCardClick ? () => onCardClick(item) : undefined}
                    >
                        {renderCard ? renderCard(item, headers) : defaultRenderCard(item)}
                    </div>
                ))}
            </div>
        )
    );
};

DataCard.propTypes = {
    headers: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            field: PropTypes.string.isRequired,
        })
    ).isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    imageField: PropTypes.string,
    titleField: PropTypes.string,
    onCardClick: PropTypes.func,
    renderCard: PropTypes.func,
    containerClassName: PropTypes.string,
    loading: PropTypes.bool,
};

export default DataCard;